
import { Breadcrumbs, CommonSelect } from "components/Common/Common";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { genericFormErrorMsg } from "utils/validation/validationHelpers";
import { Col, Container, Form, Row } from "react-bootstrap";

import styles from './profile.module.scss';
import { FieldArray, Formik } from "formik";
import { TASK_GRADE_OPTIONS } from "config/constants";
import { cloneDeep, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import taskPlanSchema from "./taskPlan.schema";
import { successMessageToast } from "helpers/common";
import { useLazyAssignedSchoolsLookupQuery } from "store/services/utility";
import CustomEditor from "components/TipTapEditor/CustomeEditor";
import AppInputFieldV2 from "components/Common/AppInputFIeldV2";
import { CrossIcon } from "Assets/icons/v2";
import { TASK_PLAN } from "config/paths";
import { useNavigate } from "react-router-dom";
import { useGetSupportStaffClientsQuery } from "store/services/V2/supportStaff";
import CommonButton from "components/Common/CommonButton";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import { useCreateTaskPlanMutation, useGetTaskPlanSchoolClientsQuery, useLazyGetTaskPlanHomeEdClientsQuery } from "store/services/V2/TaskPlan";
import moment from "moment";
import TaskPlanTemplatesPopup from "components/Modals/TaskPlanTemplates";
import { TaskPlanStrategyForm } from "components/shared/TaskPlan/TaskPlanStrategyForm";
import { useCreateTaskPlanTemplateMutation } from "store/services/V2/TaskPlanTemplate";

const AddTaskPlan = () => {
    const Navigate = useNavigate()
    const [activeClientType, setActiveClientType] = useState("school");
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedTaskIndex, setSelectedTaskIndex] = useState(null);
    const [schoolOptions, setSchoolOptions] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [clientOptions, setClientOptions] = useState({});

    const [getSchools, { isFetching: schoolsLoading }] = useLazyAssignedSchoolsLookupQuery()
    const [getHomeEdClients, { isFetching: homeEdClientsLoading }] = useLazyGetTaskPlanHomeEdClientsQuery()

    const [createTaskPlanTemplate, { isLoading: createTaskPlanLoading }] = useCreateTaskPlanTemplateMutation()
    const { currentData: supportClientList, isLoading: supportClientLoading } = useGetSupportStaffClientsQuery({
        plan_client_type: activeClientType === "home_education" ? "home_education" : "school", schoolId
    }, {skip:  activeClientType == "school" && !schoolId})
    const { data: taskSchoolClients, isLoading: taskClientLoading } = useGetTaskPlanSchoolClientsQuery({ schoolId }, { skip: !schoolId })

    const [createTaskPlan, { isLoading: createTaskLoading, isFetching: createTaskFetching }] = useCreateTaskPlanMutation()

    const getSchoolOptions = async (search) => {
        try {

            const result = await getSchools(search);
            if (result?.data?.[0]?.id) {
                const data = result?.data?.map((item) => ({ label: item?.name, value: item?.id }))
                setSchoolOptions(data);
            }
        } catch (error) { console.error("Error Getting school options"); };

    };

    useEffect(() => {
        getSchoolOptions()
    }, [])

    const getClientName = (client) => {
        const firstName = client?.firstname || client?.first_name || client?.client_detail?.firstname || "";
        const lastName = client?.lastname || client?.last_name || client?.client_detail?.lastname || "";
        return `${firstName} ${lastName}`.trim() || client?.name || client?.client_detail?.name || "Unnamed Client";
    };

    const getClientId = (client) => client?.client_detail?.id || client?.id || client?.client_id;

    const loadHomeEdClientsForGrade = async (grade) => {
        if (!grade || clientOptions[grade]) return;

        try {
            const result = await getHomeEdClients({ grade }).unwrap();
            const options = result
                ?.map((item) => ({
                    value: getClientId(item),
                    label: getClientName(item),
                }))
                ?.filter((item) => item.value);

            setClientOptions((prev) => ({
                ...prev,
                [grade]: options || []
            }));
        } catch (error) {
            console.error("Error Getting home education client options", error);
        }
    };

    const emptyTask = {
        task_title: "",
        description: "",
        // date: "",
        strategy: [{
            text: "",
            attachments: []
        }]
    }

    const initialValues = useMemo(() => {

        // ✅ 1. If template selected
        if (selectedTemplate) {
            return {
                school: "",
                client_type: activeClientType,
                support_staffs: [{ staff_id: null }],
                clients: [{ grade: "", client_id: null }],

                plan_name: selectedTemplate?.plan_name || "",
                // plan_description: selectedTemplate?.plan_description || "",

                date: selectedTemplate?.date
                    ? moment(selectedTemplate.date).format("YYYY-MM-DD")
                    : "",

                start_date: selectedTemplate?.start_date
                    ? moment(selectedTemplate.start_date).format("YYYY-MM-DD")
                    : "",

                end_date: selectedTemplate?.end_date
                    ? moment(selectedTemplate.end_date).format("YYYY-MM-DD")
                    : "",

                no_of_sessions: selectedTemplate?.no_of_sessions || "",
                tasks: selectedTemplate?.tasks?.length
                    ? selectedTemplate?.tasks?.map(task => ({
                        task_title: task?.task_title || "",
                        description: task?.description || "",
                        attachments: task?.attachments || [],
                        strategy: task.strategy
                            ? {
                                text: task?.strategy?.text || "",
                                attachments: task?.strategy?.attachments || []
                            }
                            : null
                    }))
                    : [emptyTask]
            };
        }
        else {
            return {
                school: "",
                client_type: activeClientType,
                support_staffs: [{ staff_id: null }],
                clients: [{ grade: "", client_id: null }],
                date: "",
                plan_name: "",
                tasks: [emptyTask],
                start_date: "",
                end_date: "",
                no_of_sessions: ""
            };
        }
    }, [activeClientType, selectedTemplate]);

    const handleSubmit = async (payload) => {
        try {
            const values = cloneDeep(payload);
            const formattedTasks = values?.tasks?.map((task) => {
                const { strategy, ...rest } = task;

                const formattedStrategy = strategy?.map((item) => ({
                    ...item,
                    template_id: item?.template_id?.toString(),
                    stragety_id: item?.stragety_id || "",

                }));

                return strategy
                    ? { ...rest, strategy: formattedStrategy }
                    : { ...rest };
            });

            const finalPayload = {
                ...values,
                tasks: formattedTasks,
                school: values?.school?.value,
                support_staffs: values?.support_staffs?.map((item) => item?.staff_id?.value),
                clients: values?.clients?.map((item) => (
                    {
                        grade: item?.grade?.value,
                        client_id: item?.client_id?.value
                    }
                ))
            };

            if (values?.client_type === "home_education") {
                delete finalPayload.school;
                finalPayload.client_type = "home_education";
            } else {
                delete finalPayload.client_type;
            }
            
            const result = await createTaskPlan(finalPayload).unwrap();
            if (result.error) return; 
            successMessageToast(result?.message);
            Navigate(TASK_PLAN)

        } catch (err) { console.error("Error updating profile", err); }
    };

    const handleSaveTaskTemplate = async (payload) => {
        const finalPayload = {
            attachments: payload?.attachments || [],
            task_title: payload?.task_title || "",
            date: payload?.date || "",
            description: payload?.description || "",
            strategy: payload?.strategy?.map((item) => ({
                ...item,
                template_id: item?.template_id?.toString() || "",
                stragety_id: item?.stragety_id || "",
                title: item?.title || ""

            }))
        }
        try {
            const resp = await createTaskPlanTemplate(finalPayload).unwrap()

            if (resp.status === "success") {
                successMessageToast("Template saved successfully");
            }

        } catch (error) {
            console.error(error);
        }
    }


    return (
        <>
            <div className="px-3 py-2">
                <Breadcrumbs items={[{ label: "Task Plans", path: "/client/task-plan" }, { label: 'Add Task', path: "" }]} style={{ fontSize: "14px" }} />

                <section className={`${styles.addForm} position-relative col-lg-10 addForm`}>
                    <Container fluid={true}>
                        <Row>
                            <Col lg="12" className="my-2 px-0 ">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={taskPlanSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({
                                        handleSubmit,
                                        errors,
                                        submitCount,
                                        values,
                                        setFieldValue,
                                        setFieldError,
                                        touched,
                                        setFieldTouched
                                    }) => {
                                        const handleTemplateSelect = (template) => {
                                            if (selectedTaskIndex === null) return;

                                            setFieldValue(`tasks.${selectedTaskIndex}.task_title`, template?.task_title || "");
                                            setFieldValue(`tasks.${selectedTaskIndex}.description`, template?.description || "");
                                            setFieldValue(`tasks.${selectedTaskIndex}.attachments`, template?.attachments || []);

                                            const strategies =
                                                template?.strategy?.map((item) => ({
                                                    text: item?.text || "",
                                                    attachments: item?.attachments || [],
                                                    template_id: item?.template_id || "",
                                                    strategyRadio: item?.strategyRadio || "",
                                                    title: item?.title || "",


                                                })) || [];

                                            setFieldValue(`tasks.${selectedTaskIndex}.strategy`, strategies);

                                            setShowTemplateModal(false);
                                        };



                                        const selectedStaffIds = values.support_staffs
                                            ?.map(s => s.staff_id?.value)
                                            ?.filter(Boolean);

                                        const selectedClientIds = values.clients
                                            ?.map(c => c.client_id?.value)
                                            ?.filter(Boolean);

                                        return (
                                            <>
                                                <TaskPlanTemplatesPopup
                                                    showPopup={showTemplateModal}
                                                    setShowPopup={setShowTemplateModal}
                                                    onSelectTemplate={handleTemplateSelect}
                                                    closePopup={() => setShowTemplateModal(false)}
                                                />

                                                <Form onSubmit={handleSubmit}>
                                                    <div
                                                    >
                                                        <Col lg="12" className="mb-2">
                                                            <div className=' d-flex align-items-end justify-content-between'>
                                                                <h5 className='section-title-main fw-medium border-0 m-0 p-0'>
                                                                    Add a Task Plan</h5>
                                                                {/* <CommonButton
                                                                text="Choose from My Library"
                                                                className="staff-button-outlined mt-3"
                                                                onClick={() => setShowTemplateModal(true)}
                                                            /> */}
                                                            </div>
                                                        </Col>

                                                        <div className={styles.clientTypeTabs}>
                                                            <button
                                                                type="button"
                                                                className={values.client_type === "school" ? styles.activeTab : ""}
                                                                onClick={() => {
                                                                    setActiveClientType("school");
                                                                    setFieldValue("client_type", "school");
                                                                    setFieldValue("support_staffs", [{ staff_id: null }]);
                                                                    setFieldValue("clients", [{ grade: "", client_id: null }]);
                                                                    setSchoolId(null);
                                                                }}
                                                            >
                                                                School Clients
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={values.client_type === "home_education" ? styles.activeTab : ""}
                                                                onClick={() => {
                                                                    setActiveClientType("home_education");
                                                                    setFieldValue("client_type", "home_education");
                                                                    setFieldValue("school", null);
                                                                    setSchoolId(null);
                                                                    setFieldValue("support_staffs", [{ staff_id: null }]);
                                                                    setFieldValue("clients", [{ grade: "", client_id: null }]);
                                                                }}
                                                            >
                                                                Home Education Clients
                                                            </button>
                                                        </div>

                                                        {values.client_type === "school" && (
                                                            <div style={{ marginTop: "15px" }}>
                                                                <h5 className="section-title">1.   CHOOSE A SCHOOL</h5>
                                                                <div className="d-flex flex-column gap-4 ps-md-3">
                                                                    <Row>
                                                                        <Col xs="8" lg="6">
                                                                            <div className="w-100 reports-v2 ">
                                                                                <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                                                                    School Name*
                                                                                </label>
                                                                                <CommonSelect
                                                                                    placeholder=""
                                                                                    value={values.school}
                                                                                    option={schoolOptions}
                                                                                    onChange={(val) => {
                                                                                        setFieldValue(`school`, val);
                                                                                        setSchoolId(val?.value);
                                                                                        setFieldValue(`clients`, [{ grade: "", client_id: null }]);
                                                                                        setFieldValue(`support_staffs`, [{ staff_id: null }]);
                                                                                    }}
                                                                                    isClearable
                                                                                    isFilterV2
                                                                                    isLoading={schoolsLoading}
                                                                                    name={"school"}
                                                                                />
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{ marginTop: "34px" }}>
                                                            <h5 className="section-title">{values.client_type === "school" ? "2" : "1"}. CHOOSE SUPPORT STAFF</h5>

                                                            <div className="ps-md-3">
                                                                <FieldArray name="support_staffs">
                                                                    {({ push, remove }) => (
                                                                        <>
                                                                            {values.support_staffs?.map((staff, staffIndex) => (
                                                                                <Row key={staffIndex} className="mb-3">
                                                                                    <Col xs="8" lg="6">
                                                                                        <div className="w-100 reports-v2">
                                                                                            <label
                                                                                                className="mb-1 fw-semibold"
                                                                                                style={{ fontSize: "12px" }}
                                                                                            >
                                                                                                Support Staff*
                                                                                            </label>

                                                                                            <CommonSelect
                                                                                                name={`support_staffs.${staffIndex}.staff_id`}
                                                                                                placeholder=""
                                                                                                value={values.support_staffs[staffIndex]?.staff_id || null}
                                                                                                options={
                                                                                                    supportClientList
                                                                                                        ?.map(item => ({
                                                                                                            value: item.id,
                                                                                                            label: `${item.firstname} ${item.lastname ?? ""}`,
                                                                                                        }))
                                                                                                        ?.filter(option =>
                                                                                                            !selectedStaffIds.includes(option.value) ||
                                                                                                            option.value === values.support_staffs[staffIndex]?.staff_id?.value
                                                                                                        )
                                                                                                }
                                                                                                onChange={(val) =>
                                                                                                    // console.log("val",val)
                                                                                                    setFieldValue(`support_staffs.${staffIndex}.staff_id`, val)
                                                                                                }
                                                                                                isClearable
                                                                                                isFilterV2
                                                                                                isLoading={supportClientLoading}
                                                                                            />
                                                                                        </div>
                                                                                    </Col>

                                                                                    <Col sm="2" className="d-flex align-items-end ">
                                                                                        {staffIndex > 0 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="bg-transparent border-0"
                                                                                                style={{ color: "#023843" }}
                                                                                                onClick={() => remove(staffIndex)}
                                                                                            >
                                                                                                <CrossIcon />
                                                                                            </button>
                                                                                        )}
                                                                                    </Col>
                                                                                </Row>
                                                                            ))}
                                                                            {
                                                                                values.client_type === "school" && values.support_staffs?.length < 10 &&
                                                                                <CommonButton
                                                                                    text="Add Another"
                                                                                    className="staff-button-filled mt-3"
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        push({
                                                                                            staff_id: null,
                                                                                        })
                                                                                    }
                                                                                />
                                                                            }

                                                                        </>
                                                                    )}
                                                                </FieldArray>
                                                            </div>
                                                        </div>


                                                        <div style={{ marginTop: "34px" }}>
                                                            <h5 className="section-title">{values.client_type === "school" ? "3" : "2"}.   CHOOSE CLIENT(S)</h5>
                                                            <div className="ps-md-3">
                                                                <FieldArray name="clients">
                                                                    {({ push, remove }) => (
                                                                        <>
                                                                            {values.clients && values.clients.length > 0 &&
                                                                                values.clients.map((client, clientIndex) => (
                                                                                    <Row key={clientIndex} className="mb-3">
                                                                                        <Col xs="2">
                                                                                            <div className="w-100 reports-v2">
                                                                                                <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                                                                                    Grade*
                                                                                                </label>

                                                                                                <CommonSelect
                                                                                                    name={`clients.${clientIndex}.grade`}
                                                                                                    placeholder=""
                                                                                                    value={values.clients[clientIndex].grade}
                                                                                                    options={TASK_GRADE_OPTIONS}
                                                                                                    onChange={(val) => {
                                                                                                        setFieldValue(`clients.${clientIndex}.grade`, val);
                                                                                                        setFieldValue(`clients.${clientIndex}.client_id`, null);
                                                                                                        if (values.client_type === "home_education") {
                                                                                                            loadHomeEdClientsForGrade(val?.value);
                                                                                                        }
                                                                                                    }

                                                                                                    }
                                                                                                    isClearable
                                                                                                    isFilterV2
                                                                                                />
                                                                                            </div>
                                                                                        </Col>

                                                                                        <Col xs="6" xl="7">
                                                                                            <div className="w-100 reports-v2">
                                                                                                <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                                                                                    Name*
                                                                                                </label>

                                                                                                <CommonSelect
                                                                                                    name={`clients.${clientIndex}.client_id`}
                                                                                                    placeholder=""
                                                                                                    value={values.clients[clientIndex].client_id}
                                                                                                    options={
                                                                                                        (values.client_type === "home_education"
                                                                                                            ? clientOptions?.[values?.clients[clientIndex]?.grade?.value]
                                                                                                            : taskSchoolClients
                                                                                                                ?.filter(item => item?.client_detail?.grade == values?.clients[clientIndex]?.grade?.value)
                                                                                                                ?.map(item => ({
                                                                                                                    value: item?.client_detail?.id,
                                                                                                                    label: `${item?.firstname} ${item?.lastname}`,
                                                                                                                })))
                                                                                                            ?.filter(option =>
                                                                                                                !selectedClientIds.includes(option.value) ||
                                                                                                                option?.value === values?.clients[clientIndex]?.client_id?.value
                                                                                                            )
                                                                                                    }

                                                                                                    onChange={(val) =>
                                                                                                        setFieldValue(`clients.${clientIndex}.client_id`, val)
                                                                                                    }
                                                                                                    isClearable
                                                                                                    isFilterV2
                                                                                                    isLoading={values.client_type === "home_education" ? homeEdClientsLoading : taskClientLoading}
                                                                                                />
                                                                                            </div>
                                                                                        </Col>

                                                                                        <Col md="2" className="d-flex align-items-end">
                                                                                            {
                                                                                                clientIndex > 0 &&
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className=' bg-transparent border-0'
                                                                                                    style={{ color: "#023843" }} onClick={() => remove(clientIndex)}>
                                                                                                    <CrossIcon />
                                                                                                </button>
                                                                                            }

                                                                                        </Col>
                                                                                    </Row>
                                                                                ))}

                                                                            {
                                                                                values?.clients?.length < 10 &&
                                                                                <CommonButton
                                                                                    text="Add Another"
                                                                                    className="staff-button-outlined mt-3"
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        push({
                                                                                            grade: "",
                                                                                            client_id: "",
                                                                                        })
                                                                                    }}
                                                                                />
                                                                            }
                                                                        </>
                                                                    )}
                                                                </FieldArray>
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: "34px" }}>
                                                            <div className="position-relative">
                                                                <h5 className="section-title mt-3">
                                                                    {values.client_type === "school" ? "4" : "3"}. PLAN
                                                                </h5>
                                                            </div>
                                                            <Row className='d-flex justify-content-between ps-md-3'>
                                                                <Col xs="9">
                                                                    <label className="discharge-screen-label">
                                                                        Plan Name*
                                                                    </label>
                                                                    <AppInputFieldV2
                                                                        name={`plan_name`}
                                                                        type="text"
                                                                        className="readonly-box"
                                                                    />
                                                                </Col>
                                                                <Col xs="3" xl="2" className='d-flex '>
                                                                    <div className=' reports-v2' style={{ width: "145px" }}>
                                                                        <label className="discharge-screen-label">
                                                                            Date Created*
                                                                        </label>
                                                                        <AppDatePicker
                                                                            name={`date`}
                                                                            className="read-only w-100 py-2"
                                                                            popperPlacement="bottom-start"
                                                                            popperClassName="react-datepicker-popper"
                                                                        />
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            {/* <Col md="9" className="mt-3 ps-md-3">
                                                                <label className="discharge-screen-label">
                                                                    Plan Description*
                                                                </label>
                                                                <CustomEditor
                                                                    name={`plan_description`}
                                                                />
                                                            </Col> */}
                                                        </div>

                                                        <div style={{ marginTop: "34px" }}>
                                                            <h5 className="section-title mt-3">
                                                                {values.client_type === "school" ? "5" : "4"}. ADD TASK(S)
                                                            </h5>
                                                            <FieldArray name="tasks">
                                                                {({ push, remove }) => (
                                                                    <>
                                                                        {values?.tasks?.map((task, taskIndex) => {
                                                                            return (
                                                                                <>
                                                                                    <div key={taskIndex} className="ps-md-3 mb-4 ">
                                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                                            <span className="fw-semibold text-secondary me-2">
                                                                                                {taskIndex + 1}.
                                                                                            </span>
                                                                                            <div className="">
                                                                                                {values.tasks.length > 1 && taskIndex > 0 && (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className='bg-transparent border-0'
                                                                                                        style={{ color: "#023843" }} onClick={() => remove(taskIndex)}>
                                                                                                        <CrossIcon />
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>

                                                                                        <div className="">
                                                                                            <Row className='d-flex justify-content-between align-items-start'>
                                                                                                <Col md="9">
                                                                                                    <label className="discharge-screen-label">
                                                                                                        Task Name*
                                                                                                    </label>
                                                                                                    <AppInputFieldV2
                                                                                                        name={`tasks.${taskIndex}.task_title`}
                                                                                                        type="text"
                                                                                                        className="readonly-box"
                                                                                                    />
                                                                                                </Col>

                                                                                                <Col md="3" className='d-flex flex-column align-items-end'>
                                                                                                    <CommonButton
                                                                                                        text="Choose from My Library"
                                                                                                        className="staff-button-outlined"
                                                                                                        style={{ height: "40px", marginTop: "24px" }}
                                                                                                        onClick={() => {
                                                                                                            setSelectedTaskIndex(taskIndex);
                                                                                                            setShowTemplateModal(true)
                                                                                                        }}
                                                                                                    />
                                                                                                </Col>
                                                                                            </Row>

                                                                                            <Col md="9" className="mt-3">
                                                                                                <label className="discharge-screen-label">
                                                                                                    Task Description*
                                                                                                </label>
                                                                                                <p className='support-staff-text-head' style={{ fontWeight: 400 }}>Describe the task you want the student to work on. You will be able to update this description when the task is in progress.</p>
                                                                                                <CustomEditor
                                                                                                    name={`tasks.${taskIndex}.description`}
                                                                                                    attachmentName={`tasks.${taskIndex}.attachments`}
                                                                                                />
                                                                                            </Col>

                                                                                            <div className="staff-divider mt-4"></div>
                                                                                        </div>
                                                                                        {/* <div>
                                                                                        {task.strategy?.map((item, strategyIndex) => {
                                                                                            return (
                                                                                                <div className="my-3 position-relative" key={strategyIndex}>
                                                                                                    <Col md="9">
                                                                                                        <label className="discharge-screen-label">
                                                                                                            Strategy Text {strategyIndex + 1}
                                                                                                        </label>

                                                                                                        <CustomEditor
                                                                                                            name={`tasks.${taskIndex}.strategy.${strategyIndex}.text`}
                                                                                                            attachmentName={`tasks.${taskIndex}.strategy.${strategyIndex}.attachments`}
                                                                                                        />
                                                                                                    </Col>

                                                                                                    <button
                                                                                                        className="position-absolute top-0 end-0 bg-transparent border-0"
                                                                                                        style={{ color: "#023843" }}
                                                                                                        type="button"
                                                                                                        onClick={() => {
                                                                                                            const updated = [...task.strategy];
                                                                                                            updated.splice(strategyIndex, 1);
                                                                                                            setFieldValue(`tasks.${taskIndex}.strategy`, updated);
                                                                                                        }}
                                                                                                    >
                                                                                                        <CrossIcon />
                                                                                                    </button>
                                                                                                    {
                                                                                                        task?.strategy?.length > 0 && <div className="staff-divider mt-4"></div>
                                                                                                    }
                                                                                                </div>
                                                                                            );
                                                                                        })}

                                                                                        {task?.strategy?.length < 4 && (
                                                                                            <div className="d-flex align-items-center gap-3 mt-3">
                                                                                                <CommonButton
                                                                                                    className="staff-button-outlined"
                                                                                                    text="Add a Strategy"
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        const updatedStrategies = [
                                                                                                            ...(task.strategy || []),
                                                                                                            {
                                                                                                                text: "",
                                                                                                                attachments: []
                                                                                                            }
                                                                                                        ];

                                                                                                        setFieldValue(`tasks.${taskIndex}.strategy`, updatedStrategies);
                                                                                                    }}
                                                                                                /> 
                                                                                            </div>
                                                                                        )}

                                                                                    </div> */}

                                                                                        {/* STRATEGY SECTION */}
                                                                                        <FieldArray name={`tasks.${taskIndex}.strategy`}>
                                                                                            {({ push: pushStrategy, remove: removeStrategy }) => (
                                                                                                <>
                                                                                                    {values?.tasks?.[taskIndex]?.strategy?.map((strategy, strategyIndex) => (
                                                                                                        <div key={strategyIndex} className="mb-3 mt-5 position-relative">
                                                                                                            <TaskPlanStrategyForm
                                                                                                                taskIndex={taskIndex}
                                                                                                                strategyIndex={strategyIndex}
                                                                                                                setFieldValue={setFieldValue}
                                                                                                                setFieldTouched={setFieldTouched}
                                                                                                                values={values}
                                                                                                                remove={removeStrategy}
                                                                                                                clientType="task_plan"
                                                                                                                errors={errors}
                                                                                                                touched={touched}
                                                                                                            />
                                                                                                        </div>

                                                                                                    ))}


                                                                                                    <div className="d-flex align-items-center gap-3 mt-3">
                                                                                                        {(values?.tasks?.[taskIndex]?.strategy?.length || 0) < 2 && (
                                                                                                            <CommonButton
                                                                                                                className="staff-button-outlined"
                                                                                                                text="Add a Strategy"
                                                                                                                type="button"
                                                                                                                onClick={() =>
                                                                                                                    pushStrategy({
                                                                                                                        text: "",
                                                                                                                        attachments: []
                                                                                                                    })
                                                                                                                }
                                                                                                            />

                                                                                                        )}
                                                                                                    </div>
                                                                                                    <div className="staff-divider my-4"></div>

                                                                                                    <CommonButton
                                                                                                        className="staff-button-filled"
                                                                                                        style={{ minWidth: "157px", minHeight: "42px" }}
                                                                                                        text={createTaskPlanLoading ? <LoadingIndicator /> : 'Save to Templates'}
                                                                                                        type="button"
                                                                                                        onClick={() => {
                                                                                                            handleSaveTaskTemplate(values?.tasks?.[taskIndex])
                                                                                                        }}
                                                                                                    />
                                                                                                </>
                                                                                            )}
                                                                                        </FieldArray>
                                                                                    </div>
                                                                                    {taskIndex !== values.tasks.length - 1 && (
                                                                                        <div className="staff-divider my-4"></div>
                                                                                    )}
                                                                                </>
                                                                            )
                                                                        })}
                                                                        <div className="staff-divider my-4"></div>
                                                                        <div>
                                                                            {
                                                                                values?.tasks?.length < 4 &&
                                                                                <div className='d-flex align-items-center gap-3'>
                                                                                    <CommonButton
                                                                                        className="staff-button-filled"
                                                                                        text="Add a Task"
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            if (values.tasks.length < 4) {
                                                                                                push(emptyTask)
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                    <span className='support-staff-description' style={{ color: "#071E3E", fontStyle: "italic" }}>
                                                                                        Max. 4
                                                                                    </span>
                                                                                </div>

                                                                            }
                                                                        </div>

                                                                    </>
                                                                )}
                                                            </FieldArray>
                                                        </div>
                                                        <div style={{ marginTop: "34px" }}>
                                                            <h5 className="section-title mt-5">
                                                                {values.client_type === "school" ? "6" : "5"}. SET FREQUENCY
                                                            </h5>

                                                            <Row className="ps-md-3  reports-v2">
                                                                <Col xs="3" xl="2">
                                                                    <label className="discharge-screen-label">
                                                                        Start Date
                                                                    </label>
                                                                    <AppDatePicker
                                                                        name={`start_date`}
                                                                        className="read-only w-100 py-2"
                                                                        popperPlacement="bottom-start"
                                                                        popperClassName="react-datepicker-popper"
                                                                    />
                                                                </Col>

                                                                <Col xs="3" xl="2">
                                                                    <label className="discharge-screen-label">
                                                                        End Date
                                                                    </label>
                                                                    <AppDatePicker
                                                                        name={`end_date`}
                                                                        className="read-only w-100 py-2"
                                                                        popperPlacement="bottom-start"
                                                                        popperClassName="react-datepicker-popper"
                                                                        minDate={values.start_date}
                                                                    />
                                                                </Col>

                                                                <Col xs="3" xl="2">
                                                                    <label className="discharge-screen-label">
                                                                        Number of Sessions
                                                                    </label>
                                                                    <AppInputFieldV2 name="no_of_sessions" type="text" className="readonly-box" />
                                                                </Col>
                                                            </Row>
                                                        </div>

                                                        <div className="d-flex justify-content-center align-items-center mt-5" style={{ background: "#93278F1A", borderRadius: "12px", height: "74px" }}>
                                                            <span className="support-staff-description" style={{ color: "#93278F" }}>Task Plans will become visible to the Support Staff in their dashboard.</span>
                                                        </div>
                                                        {(!isEmpty(errors) && submitCount > 0) && (
                                                            <Row className="mt-4">
                                                                <Col lg="12">
                                                                    <span className="error">{genericFormErrorMsg}</span>
                                                                </Col>
                                                            </Row>
                                                        )}
                                                        <div className="d-flex justify-content-between mt-5">
                                                            <button type="button" onClick={() => Navigate(TASK_PLAN)} className="button-outlined ">Cancel  </button>

                                                            <button type="submit" className="button-filled">{createTaskLoading ? <LoadingIndicator color="#fff" /> : 'DONE'}  </button>
                                                        </div>


                                                    </div>


                                                </Form>
                                            </>
                                        )
                                    }}
                                </Formik>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
};

export default AddTaskPlan;
